import { FormlyFieldConfig } from '@ngx-formly/core';
import {
  IFormbuilderFieldTypeConfiguration,
  ICustomIdInputFieldSettingsModel,
} from '../../../models/form-builder-types-configurations.model';
import {
  baseFieldUpdateFromSettingsFormModel,
  getDefaultBaseSettingsConfigFormly,
  getPopulatesPropsSettingsConfigFormly,
  populatesPropsParseIntoSettingsFormModel,
  populatesPropsUpdateFromSettingsFormModel,
  getPayloadKeySettingsConfigFormly,
  getPopulateRelativeToRootFieldConfig,
} from '../form-fields-formly-settings/form-builder-default-settings-configuration-formly-fields.utils';
import {
  parseRequiredValidatorParseFieldIntoModel,
  parseInvalidGenericInputValidatorParseFieldIntoModel,
} from '../form-fields-formly-validations/form-fields-formly-validation-field-parse-to-model.util';
import {
  setRequiredValidatorUpdateFieldFromModel,
  setInvalidGenericInputFormDataIntoField,
} from '../form-fields-formly-validations/form-fields-formly-validation-field-set-from-model.util';
import { FormBuilderService } from '../../../services/form-builder.service';
import { IFormFieldsDisplayModel } from '../../../models/form-fields-formly-display.model';
import { parseFieldIntoDisplayFormModel } from '../form-fields-formly-display/form-fields-formly-display-field-parse-to-model.util';
import { setDisplayHideExpressionFromModel } from '../form-fields-formly-display/form-fields-formly-display-field-set-from-model.util';
import { fieldsBaseConfigurationFormModel } from './shared.configuration';
import {
  getRequiredValidatorFormConfig,
  getValidationFieldsFormConfig,
} from '../form-fields-formly-validations/form-fields-formly-validation-field-generator.util';
import { getFieldDisplayFormConfig } from '../form-fields-formly-display/form-fields-formly-display-field-generator.util';
import { EIremboFormlyFieldTypes } from '@irembo-andela/irembogov3-common';
import { IFormFieldsValidationModel } from '../../../models/form-fields-formly-validation.model';
import {
  getConditionalUtilValidatorsFormConfig,
  getConditionalValidationsFormConfig,
} from '../form-fields-formly-validations/conditional-validations/fields-formly-conditional-validation-field-generator.util';
import { EAvailableConditionalValidations } from '../../../models/form-fields-formly-conditional-validation.model';
import {
  parseConditionalValidatorsModelFromFieldIntoModel,
  parseUtilValidatorsModelFromFieldIntoModel,
} from '../form-fields-formly-validations/conditional-validations/fields-formly-conditional-validation-field-parse-to-model.util';
import {
  setConditionalExpressionFromModel,
  setUtilConditionalExpressionFromModel,
} from '../form-fields-formly-validations/conditional-validations/fields-formly-conditional-validation-field-set-from-model.util';
import { ErrorMappingConfig } from '@irembo-andela/irembogov3-common/lib/ui/models/error-mapping.model';

interface IPayloadKeyMapping {
  key: string;
  sourceField?: string;
  isStatic?: boolean;
  staticValue?: string;
}

interface IGenericDataFetchFieldContentConfig {
  url?: string;
  useBaseUrl?: boolean;
  endpointCode?: string;
  useMultiplePayloadKeys?: boolean;
  payloadKey?: string;
  payloadKeyMappings?: IPayloadKeyMapping[];
  genericInputLength?: number;
  triggerType?: string;
  debounceTime?: number;
  hideField?: boolean;
  showGlobalError?: boolean;
  mixpanelEventAnalytics?: boolean;
}

export interface IGenericDataFetchConfigurationFormModel
  extends ICustomIdInputFieldSettingsModel {
  genericDataFetchFieldContentConfig?: IGenericDataFetchFieldContentConfig;
  defaultValue?: string;
}

export interface IGenericDataFetchValidationModel
  extends IFormFieldsValidationModel {
  errorMapping?: ErrorMappingConfig;
}

export const genericDataFetchInputGetConfigurationConfig = (
  formBuilderServiceInstance: FormBuilderService
): IFormbuilderFieldTypeConfiguration => {
  return {
    settings: {
      formModel: {
        ...fieldsBaseConfigurationFormModel(),
        url: '',
        useBaseUrl: true,
        endpointCode: '',
        useMultiplePayloadKeys: false,
        payloadKey: '',
        payloadKeyMappings: [],
        genericInputLength: 0,
        triggerType: 'INPUT_LENGTH',
        debounceTime: 500,
        defaultValue: null,
      },
      formlyForm: [
        ...getGenericDataFetchFieldSettingsConfig(formBuilderServiceInstance),
        ...getDefaultBaseSettingsConfigFormly(formBuilderServiceInstance),
        {
          key: 'defaultValue',
          type: 'input',
          props: {
            label: 'Default value',
            placeholder: 'Enter default value',
          },
          validation: {
            messages: {
              maxLength: 'Default value is longer than Input Length value',
            },
          },
          expressions: {
            'props.maxLength': field => {
              return (
                field.parent?.model?.genericDataFetchFieldContentConfig
                  ?.genericInputLength ?? null
              );
            },
          },
        },
        getPopulateRelativeToRootFieldConfig(),
        ...getPopulatesPropsSettingsConfigFormly(
          formBuilderServiceInstance,
          []
        ),
      ],
    },
    validations: {
      formModel: {},
      formlyForm: [
        getRequiredValidatorFormConfig(),
        getValidationFieldsFormConfig(formBuilderServiceInstance),
        getConditionalValidationsFormConfig(
          [EAvailableConditionalValidations.required],
          formBuilderServiceInstance
        ),
        getConditionalUtilValidatorsFormConfig(formBuilderServiceInstance),
        {
          key: 'errorMapping',
          type: EIremboFormlyFieldTypes['formly-group'],
          wrappers: ['panel-wrapper'],
          props: {
            label: 'Error Mapping',
            dragDisabled: true,
            collapsible: true,
            collapsed: true,
          },
          fieldGroup: generateErrorMappingFields(),
          expressions: {},
        },
      ],
    },
    display: {
      formModel: {},
      formlyForm: [...getFieldDisplayFormConfig(formBuilderServiceInstance)],
    },
  };
};

export const genericDataFetchFieldParseIntoSettingsFormModel = (
  genericDataFetchField: FormlyFieldConfig
): IGenericDataFetchConfigurationFormModel => {
  const model: IGenericDataFetchConfigurationFormModel =
    populatesPropsParseIntoSettingsFormModel(genericDataFetchField);

  model.genericDataFetchFieldContentConfig = {
    url: genericDataFetchField.props?.['url'],
    useBaseUrl: genericDataFetchField.props?.['useBaseUrl'],
    endpointCode: genericDataFetchField.props?.['endpointCode'],
    useMultiplePayloadKeys:
      genericDataFetchField.props?.['useMultiplePayloadKeys'],
    payloadKey: genericDataFetchField.props?.['payloadKey'],
    payloadKeyMappings: genericDataFetchField.props?.['payloadKeyMappings'],
    genericInputLength: genericDataFetchField.props?.['genericInputLength'],
    triggerType: genericDataFetchField.props?.['triggerType'] || 'INPUT_LENGTH',
    debounceTime: genericDataFetchField.props?.['debounceTime'] || 500,
    hideField: genericDataFetchField.props?.['hideField'],
    showGlobalError: genericDataFetchField.props?.['showGlobalError'],
    mixpanelEventAnalytics:
      genericDataFetchField.props?.['mixpanelEventAnalytics'],
  };

  model.defaultValue = genericDataFetchField?.defaultValue || null;

  return model;
};

export const genericDataFetchFieldUpdateFromSettingsFormModel = (
  model: IGenericDataFetchConfigurationFormModel,
  genericDataFetchField: FormlyFieldConfig
): FormlyFieldConfig => {
  genericDataFetchField = baseFieldUpdateFromSettingsFormModel(
    model,
    genericDataFetchField
  );
  genericDataFetchField = populatesPropsUpdateFromSettingsFormModel(
    model,
    genericDataFetchField
  );

  if (genericDataFetchField.props) {
    genericDataFetchField.props['url'] =
      model.genericDataFetchFieldContentConfig?.url;
    genericDataFetchField.props['useBaseUrl'] =
      model.genericDataFetchFieldContentConfig?.useBaseUrl;
    genericDataFetchField.props['endpointCode'] =
      model.genericDataFetchFieldContentConfig?.endpointCode;
    genericDataFetchField.props['useMultiplePayloadKeys'] =
      model.genericDataFetchFieldContentConfig?.useMultiplePayloadKeys;
    genericDataFetchField.props['payloadKey'] =
      model.genericDataFetchFieldContentConfig?.payloadKey;
    genericDataFetchField.props['payloadKeyMappings'] =
      model.genericDataFetchFieldContentConfig?.payloadKeyMappings;
    genericDataFetchField.props['genericInputLength'] =
      model.genericDataFetchFieldContentConfig?.genericInputLength;
    genericDataFetchField.props['triggerType'] =
      model.genericDataFetchFieldContentConfig?.triggerType;
    genericDataFetchField.props['debounceTime'] =
      model.genericDataFetchFieldContentConfig?.debounceTime;
    genericDataFetchField.props['showGlobalError'] =
      model.genericDataFetchFieldContentConfig?.showGlobalError;
    genericDataFetchField.props['mixpanelEventAnalytics'] =
      model.genericDataFetchFieldContentConfig?.mixpanelEventAnalytics;
    genericDataFetchField.props['hideField'] =
      model.genericDataFetchFieldContentConfig?.hideField;
  } else {
    genericDataFetchField.props = {
      url: model.genericDataFetchFieldContentConfig?.url,
      useBaseUrl: model.genericDataFetchFieldContentConfig?.useBaseUrl,
      endpointCode: model.genericDataFetchFieldContentConfig?.endpointCode,
      useMultiplePayloadKeys:
        model.genericDataFetchFieldContentConfig?.useMultiplePayloadKeys,
      payloadKey: model.genericDataFetchFieldContentConfig?.payloadKey,
      payloadKeyMappings:
        model.genericDataFetchFieldContentConfig?.payloadKeyMappings,
      genericInputLength:
        model.genericDataFetchFieldContentConfig?.genericInputLength,
      showGlobalError:
        model.genericDataFetchFieldContentConfig?.showGlobalError,
      mixpanelEventAnalytics:
        model.genericDataFetchFieldContentConfig?.mixpanelEventAnalytics,
      hideField: model.genericDataFetchFieldContentConfig?.hideField,
    };
  }

  genericDataFetchField.defaultValue = model.defaultValue?.trim() ?? null;

  genericDataFetchField.className = model.genericDataFetchFieldContentConfig
    ?.hideField
    ? genericDataFetchField.className
    : undefined;

  return genericDataFetchField;
};

export const genericDataFetchFieldParseIntoValidationFormModel = (
  field: FormlyFieldConfig
): IGenericDataFetchValidationModel => {
  let model: IGenericDataFetchValidationModel = {};

  model = parseRequiredValidatorParseFieldIntoModel(model, field);
  model = parseInvalidGenericInputValidatorParseFieldIntoModel(model, field);
  model = parseConditionalValidatorsModelFromFieldIntoModel(model, field);
  model = parseUtilValidatorsModelFromFieldIntoModel(model, field);

  if (field.props && field.props['errorMapping']) {
    model.errorMapping = field.props['errorMapping'];
  }

  return model;
};

export const genericDataFetchFieldUpdateFromValidationFormModel = (
  model: IGenericDataFetchValidationModel,
  genericDataFetchInput: FormlyFieldConfig
): FormlyFieldConfig => {
  genericDataFetchInput = setRequiredValidatorUpdateFieldFromModel(
    model,
    genericDataFetchInput
  );
  genericDataFetchInput = setInvalidGenericInputFormDataIntoField(
    model,
    genericDataFetchInput
  );
  genericDataFetchInput = setConditionalExpressionFromModel(
    model,
    genericDataFetchInput
  );
  genericDataFetchInput = setUtilConditionalExpressionFromModel(
    model,
    genericDataFetchInput
  );

  genericDataFetchInput = setErrorMappingValidation(
    model,
    genericDataFetchInput
  );

  return genericDataFetchInput;
};

export const genericDataFetchFieldParseFieldIntoDisplayFormModel = (
  field: FormlyFieldConfig
): IFormFieldsDisplayModel => {
  return parseFieldIntoDisplayFormModel(field);
};

export const genericDataFetchFieldUpdateFromDisplayFormModel = (
  model: IFormFieldsDisplayModel,
  field: FormlyFieldConfig
): FormlyFieldConfig => {
  return setDisplayHideExpressionFromModel(model, field);
};

export const getGenericDataFetchFieldSettingsConfig = (
  formBuilderServiceInstance: FormBuilderService
): FormlyFieldConfig[] => {
  return [
    {
      key: 'genericDataFetchFieldContentConfig',
      id: 'genericDataFetchFieldContentConfig',
      type: EIremboFormlyFieldTypes['formly-group'],
      props: {
        dragDisabled: false,
      },
      fieldGroup: [
        {
          key: 'triggerType',
          type: EIremboFormlyFieldTypes.customdropdown,
          props: {
            label: 'Trigger Type',
            required: true,
            bindLabel: 'label',
            bindValue: 'value',
            options: [
              { label: 'Input Length', value: 'INPUT_LENGTH' },
              { label: 'Trigger Button', value: 'TRIGGER_BUTTON' },
              { label: 'Field Listener', value: 'FIELD_LISTENER' },
            ],
          },
        },
        {
          key: 'debounceTime',
          type: 'input',
          hideExpression: 'model.triggerType !== "FIELD_LISTENER"',
          props: {
            type: 'number',
            label: 'Debounce Time (ms)',
            placeholder: '500',
            min: 0,
          },
          expressions: {
            hide: (field: FormlyFieldConfig) => {
              return field?.parent?.model?.triggerType !== 'FIELD_LISTENER';
            },
          },
        },
        {
          key: 'url',
          type: 'input',
          props: {
            label: 'API URL',
            required: true,
            placeholder: 'Enter API URL',
          },
          validation: {
            messages: {
              required: 'This field is required',
            },
          },
        },
        {
          key: 'endpointCode',
          type: 'input',
          props: {
            label: 'Endpoint Code',
            required: true,
            placeholder: 'Enter Endpoint Code',
          },
          validation: {
            messages: {
              required: 'This field is required',
            },
          },
        },
        ...getPayloadKeySettingsConfigFormly(formBuilderServiceInstance),
        {
          key: 'genericInputLength',
          type: 'input',
          props: {
            type: 'number',
            label: 'Input Length',
            placeholder: 'Enter Input Length',
          },
          validation: {
            messages: {
              required: 'This field is required',
            },
          },
          expressions: {
            'props.required': (field: FormlyFieldConfig) => {
              return field?.parent?.model?.triggerType === 'INPUT_LENGTH';
            },
          },
        },
        {
          key: 'useBaseUrl',
          type: 'checkbox',
          props: {
            label: 'Use Base URL',
          },
        },
        {
          key: 'hideField',
          type: EIremboFormlyFieldTypes.checkbox,
          defaultValue: false,
          props: {
            label: 'Hide Field',
            formCheck: 'switch',
          },
        },
        {
          key: 'showGlobalError',
          type: EIremboFormlyFieldTypes.checkbox,
          defaultValue: false,
          props: {
            label: 'Show Global Error',
            formCheck: 'switch',
          },
        },
        {
          key: 'mixpanelEventAnalytics',
          type: EIremboFormlyFieldTypes.checkbox,
          defaultValue: false,
          props: {
            label: 'Enable MixPanel Analytics',
            formCheck: 'switch',
          },
        },
      ],
    },
  ];
};

export const generateErrorMappingFields = (): FormlyFieldConfig[] => {
  return [
    {
      key: 'defaultErrorCode',
      defaultValue: 'invalidGenericInput',
    },
    {
      key: 'detectionType',
      type: EIremboFormlyFieldTypes.customdropdown,
      props: {
        label: 'Detection Type',
        disabled: false,
        placeholder: 'Select Detection Type',
        bindLabel: 'label',
        bindValue: 'value',
        multiple: false,
        options: [
          {
            label:
              'Advanced (Compare what API returns with what the user is expected to view)',
            value: 'advanced',
          },
          {
            label:
              'Simple (Customize error messages from the integration API responses)',
            value: 'simple',
          },
        ],
      },
    },
    {
      key: 'errorMessagePath',
      type: EIremboFormlyFieldTypes.input,
      props: {
        label: 'Error Message Path',
        disabled: false,
        required: false,
        placeholder: 'Enter error message path',
        description: 'E.g: responseMessage',
      },
      expressions: {
        'props.required':
          "model.detectionType == 'simple' || model?.useApiMessage",
        hide: "model.detectionType !== 'simple' && !model?.useApiMessage",
      },
    },
    {
      key: 'useApiMessage',
      type: EIremboFormlyFieldTypes.checkbox,
      defaultValue: false,
      props: {
        label: 'Use API Message',
        formCheck: 'switch',
      },
      expressions: {
        hide: "model.detectionType !== 'advanced'",
      },
    },
    {
      key: 'errorCode',
      type: EIremboFormlyFieldTypes.input,
      props: {
        label: 'Error Code',
        disabled: false,
        placeholder: 'Enter error message or code to target',
        description: 'E.g: Business not found',
      },
      expressions: {
        'props.required': "model.detectionType == 'simple'",
        hide: "model.detectionType !== 'simple'",
      },
    },
    {
      key: 'errorMessage',
      type: EIremboFormlyFieldTypes.input,
      props: {
        label: 'Custom Error Message',
        disabled: false,
        required: false,
        placeholder: 'Enter custom error message',
      },
      expressions: {
        hide: "model.detectionType !== 'simple'",
        'props.required': "model.detectionType == 'simple' && !field.hide",
      },
    },
    {
      key: 'errorDetection',
      type: EIremboFormlyFieldTypes.customrepeater,
      className: 'form-builder-display-rules',
      props: {
        hideHeader: true,
        required: false,
        addText: 'Add Error Detection Configuration',
        dragDisabled: true,
        template: 'displayRules',
        title: 'Configuration',
      },
      expressions: {
        hide: "field?.parent?.model?.detectionType !== 'advanced' || field?.parent?.model?.useApiMessage",
      },
      fieldArray: {
        key: 'errorDetectionConfigs',
        className: 'form-builder-display-rules-group',
        type: EIremboFormlyFieldTypes['formly-group'],
        props: {
          hideHeader: true,
          placeholder: 'Error ',
          required: false,
          dragDisabled: true,
        },
        fieldGroup: [
          {
            key: 'errorMappingHeader',
            type: 'customsectionmessagewithlink',
            props: {
              label: '',
              title: '',
              disabled: false,
              hideLabel: true,
              messageType: 'info',
              placeholder: '',
              extraMessage:
                'This section is for error mapping, mapping error code or error message from response with custom error message.',
              extraMessageAsHtml: true,
            },
          },
          {
            key: 'path',
            type: EIremboFormlyFieldTypes.input,
            props: {
              placeholder: 'Path',
              label:
                'Path (path in the reponse where the targeted error is associated with)',
              required: false,
              description: 'E.g: responseMessage',
            },
          },
          {
            key: 'errorCode',
            type: EIremboFormlyFieldTypes.input,
            props: {
              label:
                'Error Code (the value, error code or error message associated with the path in the response)',
              placeholder: 'Enter error code',
              required: false,
              description: 'E.g: Business not found',
            },
            expressions: {
              'props.required': 'model?.path != null',
            },
          },
          {
            key: 'errorMessage',
            type: EIremboFormlyFieldTypes.input,
            props: {
              label: 'Custom Error Message',
              placeholder: 'Enter custom error message',
              required: false,
            },
            expressions: {
              'props.required':
                '!field?.parent?.model?.useApiMessage && model?.path != null',
            },
          },
          {
            key: 'errorDetectionHeader',
            type: 'customsectionmessagewithlink',
            props: {
              label: '',
              title: '',
              disabled: false,
              hideLabel: true,
              messageType: 'info',
              placeholder: '',
              extraMessage:
                'This section is for error detection, specifying properties in the response for comparison while detcting errors',
              extraMessageAsHtml: true,
            },
          },
          {
            key: 'comparator',
            type: EIremboFormlyFieldTypes.customdropdown,
            props: {
              label: 'Comparator',
              required: false,
              placeholder: 'Select comparator',
              bindLabel: 'label',
              bindValue: 'value',
              options: [
                {
                  value: '===',
                  label: 'Equals',
                },
                {
                  value: '!=',
                  label: 'Not equals',
                },
                {
                  value: '>',
                  label: 'Greater than',
                },
                {
                  value: '<',
                  label: 'Less than',
                },
                {
                  value: 'includes',
                  label: 'Includes',
                },
                {
                  value: 'startsWith',
                  label: 'Starts With',
                },
                {
                  value: 'endsWith',
                  label: 'Ends With',
                },
              ],
            },
          },
          {
            key: 'comparatorOptions',
            type: EIremboFormlyFieldTypes['formly-group'],
            props: {
              hideHeader: true,
              required: false,
            },
            fieldGroup: [
              {
                key: 'prop1',
                type: EIremboFormlyFieldTypes.input,
                props: {
                  placeholder: 'Property to compare against',
                  label:
                    'Property to compare against (property or path in the response where the targetted error is located)',
                  required: true,
                  description: 'E.g: responseMessage',
                },
              },
              {
                key: 'prop2',
                type: EIremboFormlyFieldTypes.input,
                props: {
                  placeholder: 'Value to compare against',
                  label:
                    'Value to compare against (the actual value of the property or path in the response we are targetting)',
                  required: true,
                  description: 'E.g: Business not found',
                },
              },
              {
                key: 'operator',
                type: EIremboFormlyFieldTypes.customdropdown,
                props: {
                  placeholder: 'Select operator',
                  label: 'Operator',
                  required: false,
                  hint: 'Oparator used to transform value to compare against',
                  options: [
                    { value: 'parseInt', label: 'Convert value to number' },
                    {
                      value: 'toLowerCase',
                      label: 'Convert value to lower case',
                    },
                    {
                      value: 'toUpperCase',
                      label: 'Convert value to upper case',
                    },
                  ],
                  bindValue: 'value',
                  bindLabel: 'label',
                },
              },
            ],
          },
        ],
      },
    },
  ];
};

export const setErrorMappingValidation = (
  model: IGenericDataFetchValidationModel,
  field: FormlyFieldConfig
) => {
  if (model.errorMapping) {
    field.props = {
      ...field.props,
      ['errorMapping']: model.errorMapping,
    };

    const errorMapping = model.errorMapping;
    const errorCode = errorMapping['errorCode'];
    const errorMessage = errorMapping['errorMessage'];
    const validationMessages: Record<string, string> = {};

    if (field.validation && field.validation.messages) {
      if (errorCode && errorMessage) {
        validationMessages[errorCode] = errorMessage;
      }
      if (errorMapping.errorDetection?.length !== 0) {
        errorMapping?.errorDetection?.forEach(err => {
          if (err.errorCode && err.errorMessage) {
            validationMessages[err.errorCode] = err.errorMessage;
          }
        });
      }
      field.validation.messages = {
        ...field.validation.messages,
        ...validationMessages,
      };
    }
  }

  return field;
};
